import type { VNode } from "preact";
import { useEffect } from "preact/hooks";
import { SettingsPanel } from "./SettingsPanel";
import { navigate } from "../lib/nav";
import type { Toast } from "./Toast";

type Props = {
  onClose: () => void;
  redirectX: boolean;
  onRedirectX: (on: boolean) => void;
  customSource: string;
  onCustomSource: (url: string) => void;
  preferred: string | null;
  onClearPreferred: () => void;
  onToast?: (t: Omit<Toast, "id">) => void;
  onStorageChanged?: () => void;
};

/**
 * Settings dialog — the quick popup opened by the header gear button.
 * The actual settings UI lives in SettingsPanel, shared verbatim with the
 * standalone /settings page (see SettingsPage); this wrapper only provides
 * the modal chrome: backdrop, Escape-to-close and a link to the full page.
 */
export function SettingsModal({
  onClose,
  redirectX,
  onRedirectX,
  customSource,
  onCustomSource,
  preferred,
  onClearPreferred,
  onToast,
  onStorageChanged,
}: Props): VNode {
  // Escape closes the dialog (backdrop click is wired directly below).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="modal__head">
          <h2>Settings</h2>
          <div class="modal__head-actions">
            <a
              class="btn btn--ghost btn--sm modal__page-link"
              href="/settings"
              title="Open settings as a full page"
              onClick={(e) => {
                // Hand over to the /settings page without a full reload.
                e.preventDefault();
                onClose();
                navigate("/settings");
              }}
            >
              Full page
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M7 17 17 7" /><path d="M8 7h9v9" />
              </svg>
            </a>
            <button class="btn btn--ghost btn--icon" onClick={onClose} aria-label="Close settings">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div class="modal__body">
          <SettingsPanel
            redirectX={redirectX}
            onRedirectX={onRedirectX}
            customSource={customSource}
            onCustomSource={onCustomSource}
            preferred={preferred}
            onClearPreferred={onClearPreferred}
            onToast={onToast}
            onStorageChanged={onStorageChanged}
          />
        </div>
      </div>
    </div>
  );
}
